import { useEffect, useState } from 'react';
import { Button, Modal, Skeleton } from '@hrms/ui';
import { SALARY_PIN_LOCK_SECONDS, type SalaryPinRequiredDetails } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import {
  SalaryPinCreateForm,
  SalaryPinEnterForm,
  SalaryPinForgotFlow,
  SalaryPinLockedNotice,
} from './SalaryPinForms.js';

export const SALARY_PIN_STATUS_QUERY_KEY = ['salary-pin', 'status'] as const;

type Step = 'loading' | 'create' | 'enter' | 'locked' | 'forgot' | 'impersonation' | 'failed';

const TITLES: Record<Step, string> = {
  loading: 'Salary PIN',
  create: 'Create your salary PIN',
  enter: 'Enter your salary PIN',
  locked: 'Salary PIN locked',
  forgot: 'Reset your salary PIN',
  impersonation: 'Pay details are locked',
  failed: 'Salary PIN',
};

function isFuture(instant: string | null | undefined): instant is string {
  return !!instant && Date.parse(instant) > Date.now();
}

/**
 * The prompt the API asks for when it refuses other people's pay (ADR 0041). It reads the caller's own
 * PIN state when it opens and picks the step: create a PIN (first time), enter it, wait out a lockout,
 * reset it by SMS (adding a number first when there is none), or — in a support session — explain that pay stays locked.
 *
 * `onUnlocked` carries the instant the server's unlock ends; the host passes it to `resolveUnlock`, which
 * sends every refused request again. `onCancel` hands them their 403.
 */
export function SalaryPinDialog({
  open,
  details,
  onUnlocked,
  onCancel,
}: {
  open: boolean;
  /** Why the API refused; seeds the step before the status arrives. */
  details: SalaryPinRequiredDetails | null;
  onUnlocked: (unlockedUntil: string) => void;
  onCancel: () => void;
}) {
  const status = tsr.salaryPin.getStatus.useQuery({
    queryKey: SALARY_PIN_STATUS_QUERY_KEY,
    enabled: open,
    // Fresh every time the prompt opens: the attempts and the lockout change between prompts.
    staleTime: 0,
    gcTime: 0,
  });
  const [forgot, setForgot] = useState(false);
  // A lockout reported by verify, ahead of the next status read.
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setForgot(false);
    setLockedUntil(null);
  }, [open]);

  const body = status.data?.status === 200 ? status.data.body : null;

  const visibleLockedUntil = isFuture(lockedUntil)
    ? lockedUntil
    : isFuture(body?.lockedUntil)
      ? body.lockedUntil
      : null;

  let step: Step;
  if (details?.reason === 'impersonation' || (body && !body.canUnlock)) step = 'impersonation';
  else if (!body) step = status.isError ? 'failed' : 'loading';
  else if (forgot) step = 'forgot';
  else if (visibleLockedUntil) step = 'locked';
  else if (!body.isSet) step = 'create';
  else step = 'enter';

  return (
    <Modal open={open} title={TITLES[step]} onClose={onCancel} showCloseButton trapFocus>
      {step === 'loading' && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-11" />
        </div>
      )}

      {step === 'failed' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-fg-muted">
            Could not check your salary PIN. Check your connection and try again.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onCancel}>
              Close
            </Button>
            <Button variant="primary" onClick={() => void status.refetch()}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {step === 'impersonation' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-fg-muted">
            Pay details stay locked in a support session. Only the person themselves can unlock them
            with their own salary PIN.
          </p>
          <div className="flex justify-end">
            <Button variant="primary" onClick={onCancel}>
              Close
            </Button>
          </div>
        </div>
      )}

      {step === 'create' && <SalaryPinCreateForm onUnlocked={onUnlocked} onCancel={onCancel} />}

      {step === 'enter' && body && (
        <SalaryPinEnterForm
          attemptsRemaining={body.attemptsRemaining}
          onUnlocked={onUnlocked}
          onLocked={(until) => {
            // The refusal carries the instant; if it ever does not, assume the full lock.
            setLockedUntil(
              until ?? new Date(Date.now() + SALARY_PIN_LOCK_SECONDS * 1000).toISOString(),
            );
            void status.refetch();
          }}
          onForgot={() => setForgot(true)}
          onCancel={onCancel}
        />
      )}

      {step === 'locked' && (
        <SalaryPinLockedNotice
          lockedUntil={visibleLockedUntil}
          onExpire={() => {
            setLockedUntil(null);
            void status.refetch();
          }}
          onForgot={() => setForgot(true)}
          onCancel={onCancel}
        />
      )}

      {step === 'forgot' && body && (
        <SalaryPinForgotFlow
          reset={body.reset}
          onUnlocked={onUnlocked}
          onLocked={(until) => {
            // Wrong passwords count toward the PIN lockout: back to the countdown.
            setForgot(false);
            setLockedUntil(
              until ?? new Date(Date.now() + SALARY_PIN_LOCK_SECONDS * 1000).toISOString(),
            );
            void status.refetch();
          }}
          onBack={() => setForgot(false)}
        />
      )}
    </Modal>
  );
}
